import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, ChevronDown, ChevronRight, FileText, Shield, Lock, 
  BookOpen, LogOut, Camera, X, Upload, Settings, Folder,
  ClipboardList, Library, ExternalLink, LifeBuoy, Gamepad2,
  FileCheck2, ClipboardCheck, UtensilsCrossed
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { UserAvatar, resolveAvatar } from '../lib/avatar';

// Animal avatar options
const ANIMAL_AVATARS = [
  { id: 'dog', name: 'Dog', icon: '🐕' },
  { id: 'cat', name: 'Cat', icon: '🐈' },
  { id: 'lion', name: 'Lion', icon: '🦁' },
  { id: 'tiger', name: 'Tiger', icon: '🐯' },
  { id: 'bear', name: 'Bear', icon: '🐻' },
  { id: 'panda', name: 'Panda', icon: '🐼' },
  { id: 'wolf', name: 'Wolf', icon: '🐺' },
  { id: 'fox', name: 'Fox', icon: '🦊' },
  { id: 'koala', name: 'Koala', icon: '🐨' },
  { id: 'elephant', name: 'Elephant', icon: '🐘' },
  { id: 'monkey', name: 'Monkey', icon: '🐵' },
  { id: 'eagle', name: 'Eagle', icon: '🦅' },
  { id: 'owl', name: 'Owl', icon: '🦉' },
  { id: 'dolphin', name: 'Dolphin', icon: '🐬' },
  { id: 'turtle', name: 'Turtle', icon: '🐢' },
  { id: 'butterfly', name: 'Butterfly', icon: '🦋' }
];

function AvatarMenu({ size = 'small' }) {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Check if user is superadmin
  const isSuperAdmin = user?.role === 'superadmin' || 
    user?.email === 'graeme@tudor8.com' || 
    user?.email === 'seamus@boldacious.com.au';

  // Resolved-avatar descriptor is shared with the profile-page picker and
  // any future avatar consumer via /lib/avatar.js
  const avatarDisplay = resolveAvatar(user);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      await updateAvatar(e.target.result);
      setIsUploading(false);
      setShowAvatarModal(false);
    };
    reader.readAsDataURL(file);
  };

  const selectAnimalAvatar = async (emoji) => {
    setIsUploading(true);
    await updateAvatar(`emoji:${emoji}`);
    setIsUploading(false);
    setShowAvatarModal(false);
  };

  const updateAvatar = async (avatarData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/update-avatar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ avatar: avatarData })
      });

      if (response.ok) {
        toast.success('Avatar updated!');
        await checkAuth();
      } else {
        toast.error('Failed to update avatar');
      }
    } catch (error) {
      toast.error('Failed to update avatar');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const avatarSize = size === 'large' ? 'w-24 h-24' : 'w-10 h-10';
  const iconSize = size === 'large' ? 'w-12 h-12' : 'w-6 h-6';
  const emojiSize = size === 'large' ? 'text-5xl' : 'text-2xl';

  return (
    <div className="relative">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`${avatarSize} rounded-full border-2 border-blue-200 overflow-hidden bg-blue-50 hover:border-blue-400 transition-colors flex items-center justify-center cursor-pointer`}
            data-testid="avatar-button"
          >
            <UserAvatar
              user={user}
              size={size === 'large' ? 'xl' : 'md'}
              className="border-0"
              testId="avatar-menu-current"
            />
          </button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-semibold text-zinc-900">{user?.name}</p>
              <p className="text-xs text-zinc-500">{user?.email}</p>
              <p className="text-xs text-blue-600 mt-1">Week {user?.current_week <= 0 ? '2 Week Window' : user?.current_week}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* 1. Profile and Settings */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="profile-settings-menu">
              <Settings className="w-4 h-4 mr-2" />
              Profile & Settings
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                <User className="w-4 h-4 mr-2" />
                View Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowAvatarModal(true)} data-testid="edit-avatar-link">
                <Camera className="w-4 h-4 mr-2" />
                Edit Avatar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                <Settings className="w-4 h-4 mr-2" />
                App Settings
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          
          {/* 2. Personal History (with Case Management dropdown) */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="personal-history-menu">
              <FileText className="w-4 h-4 mr-2" />
              Personal History
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={() => navigate('/personal-history')} data-testid="personal-history-link">
                <ClipboardList className="w-4 h-4 mr-2" />
                Admission Assessment
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/case-management')} data-testid="case-management-link">
                <Folder className="w-4 h-4 mr-2" />
                Case Management Profile
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          
          {/* 3. Dietary Preferences: allergies / cuisines / dislikes / medical */}
          <DropdownMenuItem onClick={() => navigate('/diet-recovery')} data-testid="dietary-preferences-link">
            <UtensilsCrossed className="w-4 h-4 mr-2 text-emerald-600" />
            Dietary Preferences
          </DropdownMenuItem>

          {/* 4. Documentation (with legal docs dropdown) */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="documentation-menu">
              <Library className="w-4 h-4 mr-2" />
              Documentation
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={() => navigate('/terms-of-service')} data-testid="terms-link">
                <FileText className="w-4 h-4 mr-2" />
                Terms of Service
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/hipaa-privacy')} data-testid="hipaa-link">
                <Shield className="w-4 h-4 mr-2" />
                HIPAA & Privacy
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/privacy-policy')} data-testid="privacy-policy-link">
                <Lock className="w-4 h-4 mr-2" />
                Privacy Policy
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/legal-review')} data-testid="legal-review-link">
                <FileText className="w-4 h-4 mr-2" />
                Legal Review (CJS)
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          
          {/* 4. User Manual */}
          <DropdownMenuItem onClick={() => navigate('/user-manual')} data-testid="user-manual-link">
            <BookOpen className="w-4 h-4 mr-2" />
            User Manual
          </DropdownMenuItem>
          
          {/* 5. References */}
          <DropdownMenuItem onClick={() => navigate('/references')} data-testid="references-link">
            <ExternalLink className="w-4 h-4 mr-2" />
            References
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Sep 2026: "Get help now": mandated public entry to the
              support-services directory (Part 5 / Part 6). */}
          <DropdownMenuItem onClick={() => navigate('/get-help')} data-testid="get-help-now-link">
            <LifeBuoy className="w-4 h-4 mr-2 text-rose-600" />
            <span className="font-semibold text-rose-700">Get help now</span>
          </DropdownMenuItem>

          {/* 6. Relapse Recovery: quietly available for those who need it. */}
          <DropdownMenuItem onClick={() => navigate('/relapse-recovery')} data-testid="relapse-recovery-link">
            <LifeBuoy className="w-4 h-4 mr-2 text-amber-600" />
            <span>Relapse Recovery</span>
          </DropdownMenuItem>

          {/* 7. Meeting Log: physical AA/NA meetings (dual-selfie + GPS) */}
          <DropdownMenuItem onClick={() => navigate('/meeting-log')} data-testid="meeting-log-link">
            <ClipboardCheck className="w-4 h-4 mr-2 text-stone-700" />
            <span>Meeting Log</span>
          </DropdownMenuItem>

          {/* 8. My Drug Screens: hidden until a real pathology partner is connected (ARP brief Feb 2026). */}

          {/* 9. My Reports: Court / Case Mgmt / Sponsor / Employer / Insurance */}
          <DropdownMenuItem onClick={() => navigate('/my-reports')} data-testid="my-reports-link">
            <FileCheck2 className="w-4 h-4 mr-2 text-indigo-700" />
            <span>My Reports</span>
          </DropdownMenuItem>

          {/* 10. Take a Break: Games */}
          <DropdownMenuItem onClick={() => navigate('/games')} data-testid="take-a-break-link">
            <Gamepad2 className="w-4 h-4 mr-2 text-emerald-600" />
            <span>Take a Break  ·  Games</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          
          {/* 8. SuperAdmin (only for Graeme and Seamus) */}
          {isSuperAdmin && (
            <>
              <DropdownMenuItem onClick={() => navigate('/superadmin')} data-testid="superadmin-link">
                <Shield className="w-4 h-4 mr-2 text-red-600" />
                <span className="text-red-600 font-medium">SuperAdmin</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          
          {/* 9. Logout */}
          <DropdownMenuItem onClick={handleLogout} data-testid="logout-link">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Avatar Edit Modal */}
      {showAvatarModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="avatar-modal">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Edit Your Avatar</h2>
              <button onClick={() => setShowAvatarModal(false)} className="text-white/80 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="flex justify-center mb-6">
                <UserAvatar user={user} size="xl" className="border-4 border-blue-200" />
              </div>
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-lg mb-4"
              >
                <Upload className="w-5 h-5 mr-2" />
                {isUploading ? 'Uploading...' : 'Upload Your Photo'}
              </Button>
              
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or choose an animal</span>
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-3">
                {ANIMAL_AVATARS.map((animal) => (
                  <button
                    key={animal.id}
                    onClick={() => selectAnimalAvatar(animal.icon)}
                    disabled={isUploading}
                    className={`w-full aspect-square rounded-xl border-2 flex flex-col items-center justify-center transition-all hover:scale-105
                      ${avatarDisplay?.value === animal.icon ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300'}
                    `}
                  >
                    <span className="text-3xl mb-1">{animal.icon}</span>
                    <span className="text-[10px] text-gray-600">{animal.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="border-t px-6 py-4 bg-gray-50">
              <Button onClick={() => setShowAvatarModal(false)} variant="outline" className="w-full">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AvatarMenu;
